import mapboxgl from 'mapbox-gl';

/**
 * Marcador do ponto buscado na barra de endereço, compartilhado entre os mapas Mapbox.
 *
 * `mapboxgl.Marker` é um overlay DOM: sobrevive à troca de estilo do mapa, então não
 * precisa ser reaplicado como as camadas GeoJSON.
 *
 * Contrato do hospedeiro: guardar o retorno de `syncAddressMarker`/`removeAddressMarker`
 * em um campo próprio (`this.addressMarker`) e limpá-lo junto com o resto do mapa.
 */

const COR_MARCADOR = '#5B7537';

/**
 * Tipos do Google Places que identificam um ponto específico — endereço, imóvel ou
 * estabelecimento. Cidade, bairro, estado e CEP ficam de fora de propósito: para uma
 * área o "centro" não significa nada, e o cliente não quer marcador nesses casos.
 *
 * @type {string[]}
 */
const TIPOS_DE_PONTO = [
    'street_address',
    'premise',
    'subpremise',
    'route',
    'intersection',
    'establishment',
    'point_of_interest',
];

/**
 * @param {{types?: string[]}} place Resultado do Google Places Autocomplete.
 * @returns {boolean} `true` para um endereço/ponto, `false` para cidade, bairro ou estado.
 */
export const isPontoEspecifico = (place) => (place?.types || []).some((tipo) => TIPOS_DE_PONTO.includes(tipo));

/**
 * @param {mapboxgl.Marker|null} marker
 * @returns {null} Para reatribuir ao campo do hospedeiro.
 */
export const removeAddressMarker = (marker) => {
    marker?.remove();
    return null;
};

/**
 * Põe o marcador no ponto buscado — ou tira o que estiver no mapa, quando a busca foi
 * por uma cidade/região.
 *
 * @param {mapboxgl.Map|null} map
 * @param {mapboxgl.Marker|null} marker Marcador da busca anterior, se houver.
 * @param {{types?: string[], formatted_address?: string, name?: string}} place
 * @param {number} lat
 * @param {number} lng
 * @returns {mapboxgl.Marker|null} O marcador atual, para o hospedeiro guardar.
 */
export const syncAddressMarker = (map, marker, place, lat, lng) => {
    const anterior = removeAddressMarker(marker);

    if (!map || !isPontoEspecifico(place)) {
        return anterior;
    }

    const novo = new mapboxgl.Marker({ color: COR_MARCADOR }).setLngLat([lng, lat]).addTo(map);
    novo.getElement().title = place.formatted_address || place.name || '';

    return novo;
};
